import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { GoogleMap, useJsApiLoader, MarkerF, InfoWindowF } from "@react-google-maps/api";
import { FaPhone, FaEnvelope, FaMapMarkerAlt } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import markerIcon from "../../../resource/image/marker.svg";

const mapContainerStyle = {
  width: "100%",
  height: "500px",
};

const getContinentsFromDistributors = (distributors) => {
  const continentsMap = {};

  distributors.forEach(({ continente, country }) => {
    if (!continentsMap[continente]) {
      continentsMap[continente] = [];
    }
    if (!continentsMap[continente].includes(country)) {
      continentsMap[continente].push(country);
    }
  });

  return continentsMap;
};

const BottomSheet = ({ distributor, onClose, t }) => {
  if (!distributor) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        transition={{ duration: 0.3 }}
        className="fixed bottom-0 left-0 right-0 bg-white shadow-2xl rounded-t-2xl p-6 z-[100]"
      >
        <div className="space-y-2 relative">
          <button
            onClick={onClose}
            className="absolute top-2 right-4 text-gray-600 text-2xl"
          >
            ✕
          </button>
          <h4 className="font-semibold text-gray-900">{distributor.name}</h4>
          <p className="text-gray-700 flex items-center">
            <FaMapMarkerAlt className="text-color-main mr-2" /> {distributor.address}
          </p>
          <p className="text-main-700 flex items-center">
            <FaPhone className="text-color-main mr-2" /> {distributor.phone}
          </p>
          <p className="text-gray-700 flex items-center">
            <FaEnvelope className="text-color-main mr-2" /> {distributor.email}
          </p>
          <div className="py-4 flex justify-center space-x-4">
            <a
              href={`mailto:${distributor.email}`}
              className="px-4 py-2 bg-color-main text-white rounded-lg flex items-center space-x-2 hover:bg-opacity-90"
            >
              <FaEnvelope /> <span>{t("sections.send_email")}</span>
            </a>
            <a
              href={`tel:${distributor.phone}`}
              className="px-4 py-2 border border-color-main text-color-main rounded-lg flex items-center space-x-2 hover:bg-color-main hover:text-white"
            >
              <FaPhone /> <span>{t("sections.call")}</span>
            </a>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

const FindDistributorSection = ({ siteConfig }) => {
  const { t } = useTranslation();

  const distributors = useMemo(() => {
    return (
      siteConfig?.clients?.map((client) => ({
        ...client,
        position: {
          lat: parseFloat(client.latitude) || 0,
          lng: parseFloat(client.longitude) || 0,
        },
      })) ?? []
    );
  }, [siteConfig]);

  const continents = getContinentsFromDistributors(distributors);
  const [selectedContinent, setSelectedContinent] = useState(
    Object.keys(continents)[0] || ""
  );
  const [selectedCountry, setSelectedCountry] = useState(
    continents[selectedContinent]?.[0] || ""
  );
  const [selectedDistributor, setSelectedDistributor] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: 0, lng: 0 });
  const [zoom, setZoom] = useState(5);
   
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: "AIzaSyAjs0VYLENiPNR8U25WVe_RogtLGVqDu7U",
  });

  useEffect(() => {
    const distributorsInCountry = distributors.filter(
      (d) => d.country === selectedCountry
    );

    if (distributorsInCountry.length > 0) {
      const newCenter = distributorsInCountry[0].position;
      if (newCenter.lat !== mapCenter.lat || newCenter.lng !== mapCenter.lng) {
        setMapCenter(newCenter);
      }

      if (zoom !== 7) {
        setZoom(7);
      }
    }
  }, [selectedCountry, distributors, mapCenter, zoom]);

  const onLoad = useCallback(function callback(map) {
    map.setZoom(zoom);
    //setMap(map);
  }, [zoom]);

  const onUnmount = useCallback(function callback() {
   // setMap(null);
  }, []);

  if (!isLoaded) {
    return <p>Loading Map...</p>;
  }

  return (
    <section className="mx-auto py-16" id="find-us">
      <div className="mx-auto px-6 py-16 flex flex-col lg:flex-row items-center bg-gray-100">
        <div className="lg:w-1/2 text-left">
          <h4 className="text-main-100 uppercase tracking-widest sec-head-font">
            {t("sections.map")}
          </h4>
          <h2 className="text-3xl sec-breif-font text-bf-100 mt-2">
            {t("sections.map_title")}
          </h2>
          <p className="text-desc-100 sec-desc-font mt-2">
            {t("sections.map_description")}
          </p>
        </div>

        <div className="lg:w-1/2 flex justify-center mt-10 lg:mt-0 relative">
          <div className="mt-4 flex space-x-4">
            <a
              href={`mailto:${siteConfig?.general?.email || ""}`}
              className="px-4 py-2 bg-color-main text-white rounded-lg flex items-center gap-2 hover:bg-color-main hover:underline hover:text-gray-200 transition duration-300"
            >
              <FaEnvelope className="text-lg" />
              <span>{t("sections.send_email")}</span>
            </a>
            <a
              href={`tel:${siteConfig?.general?.phone || "+390239540787"}`}
              className="px-4 py-2 border border-color-main text-color-main rounded-lg flex items-center space-x-2 hover:bg-blue-100"
            >
              <FaPhone className="text-lg relative top-[1px]" />
              <span>{t("sections.call")}</span>
            </a>
          </div>
        </div>
      </div>

      <section className="mx-auto py-0">
        <div className="relative">
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={mapCenter}
            zoom={zoom}
            onLoad={onLoad}
            onUnmount={onUnmount}
            options={{
              streetViewControl: false,
              fullscreenControl: false,
              mapTypeControl: false,
            }}
          >
            {distributors
              .filter((d) => d.country === selectedCountry)
              .map((dist, index) => (
                <MarkerF
                  key={dist.id || index}
                  position={dist.position}
                  icon={{
                    url: markerIcon,
                    scaledSize: new window.google.maps.Size(70, 70),
                  }}
                  options={{ clickable: true }}
                  onClick={() => setSelectedDistributor(dist)}
                />
              ))}

            {selectedDistributor && (
              <>
                {window.innerWidth < 768 ? (
                  <BottomSheet
                    distributor={selectedDistributor}
                    onClose={() => setSelectedDistributor(null)}
                    t={t}
                  />
                ) : (
                  <InfoWindowF
                    key={selectedDistributor.id}
                    position={{
                      lat: parseFloat(selectedDistributor.latitude),
                      lng: parseFloat(selectedDistributor.longitude),
                    }}
                    onCloseClick={() => setSelectedDistributor(null)}
                  >
                    <div className="space-y-2">
                      <h4 className="font-semibold text-gray-900">{selectedDistributor.name}</h4>
                      <p className="text-gray-700 flex items-center space-x-2">
                        <FaMapMarkerAlt className="text-color-main" />
                        <span>{selectedDistributor.address}</span>
                      </p>
                      <p className="text-main-700 flex items-center space-x-2">
                        <FaPhone className="text-color-main" />
                        <span>{selectedDistributor.phone}</span>
                      </p>
                      <p className="text-gray-700 flex items-center space-x-2">
                        <FaEnvelope className="text-color-main" />
                        <span>{selectedDistributor.email}</span>
                      </p>
                      <div className="py-4 px-6 flex justify-center space-x-4">
                        <a
                          href={`mailto:${selectedDistributor.email}`}
                          className="px-4 py-2 bg-color-main text-white rounded-lg flex items-center space-x-2 hover:bg-opacity-90"
                        >
                          <FaEnvelope /> <span>{t("sections.send_email")}</span>
                        </a>
                        <a
                          href={`tel:${selectedDistributor.phone}`}
                          className="px-4 py-2 border border-color-main text-color-main rounded-lg flex items-center space-x-2 hover:bg-color-main hover:text-white"
                        >
                          <FaPhone /> <span>{t("sections.call")}</span>
                        </a>
                      </div>
                    </div>
                  </InfoWindowF>
                )}
              </>
            )}
          </GoogleMap>

          <div className="absolute left-3 top-3 z-[5] bg-white w-full max-w-[90%] mx-auto sm:max-w-[300px] md:max-w-[300px] lg:max-w-[300px] p-6 rounded-lg shadow-lg md:left-3 md:top-3">
            <h4 className="font-semibold text-bf-100 map-title-font">
              {t("sections.finder_title")}
            </h4>
            <div className="flex flex-row flex-wrap gap-2 mt-4 sm:flex-col">
              <div className="flex-1">
                <label className="block text-sm font-medium">
                  {t("sections.continent")}
                </label>
                <select
                  className="w-full border rounded-lg p-2"
                  value={selectedContinent}
                  onChange={(e) => {
                    setSelectedContinent(e.target.value);
                    setSelectedCountry(continents[e.target.value]?.[0] || "");
                  }}
                >
                  {Object.keys(continents).map((continent) => (
                    <option key={continent} value={continent}>
                      {continent}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium">
                  {t("sections.country")}
                </label>
                <select
                  className="w-full border rounded-lg p-2"
                  value={selectedCountry}
                  onChange={(e) => setSelectedCountry(e.target.value)}
                >
                  {continents[selectedContinent]?.map((country) => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </section>
    </section>
  );
};

export default FindDistributorSection;
